<script setup>
import { computed, defineProps, defineEmits, ref, watch, useId } from 'vue';
import DOMPurify from 'dompurify';
import ButtonLoading from '@/components/button-loading.vue';

const props = defineProps({
    show: {
        type: Boolean,
        default: false
    },
    title: {
        type: String,
        default: 'Confirmation'
    },
    message: {
        type: String,
        required: true
    },
    confirmText: {
        type: String,
        default: 'Confirm'
    },
    cancelText: {
        type: String,
        default: 'Cancel'
    },
    showCloseBtn: {
        type: Boolean,
        default: true
    },
    isLoading: {
        type: Boolean,
        default: false
    },
    loadingText: {
        type: String,
        default: 'Loading...'
    },
    dataTestId: {
        type: String,
        default: ''
    }
});

const emit = defineEmits(['close', 'confirm']);
const alertRef = ref(null);
const confirmBtnRef = ref(null);
const cancelBtnRef = ref(null);
const focusableElements = ref([]);

const uniqueId = useId();

watch(() => props.show, (newVal) => {
    if (newVal) {
        updateFocusableElements();
        if (confirmBtnRef.value) {
            confirmBtnRef.value.focus();
        } else if (cancelBtnRef.value) {
            cancelBtnRef.value.focus();
        } else if (focusableElements.value.length > 0) {
            focusableElements.value[0].focus();
        }
    }
});

const handleConfirm = async () => {
    emit('confirm');
};

const handleClose = () => {
    emit('close');
};

const updateFocusableElements = () => {
    if (!alertRef.value) return;
    focusableElements.value = Array.from(
        alertRef.value.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
    );
};

// Sanitize all HTML content rendered through v-html to prevent XSS.
// We intentionally allow only the tags/attrs we need for this modal's layout.
const sanitizeHtml = (unsafeHtml) => DOMPurify.sanitize(unsafeHtml ?? '', {
    ALLOWED_TAGS: ['span', 'div', 'br'],
    ALLOWED_ATTR: ['class'],
    FORBID_TAGS: ['style', 'script', 'iframe', 'object', 'embed', 'link', 'meta'],
    FORBID_ATTR: ['style', 'onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'srcset', 'href', 'xlink:href']
});

const safeMessage = computed(() => sanitizeHtml(props.message));
const safeConfirmText = computed(() => sanitizeHtml(props.confirmText));
const safeCancelText = computed(() => sanitizeHtml(props.cancelText));
</script>

<template>
    <Teleport to="body">
        <div v-if="show" class="alert-overlay" data-modal :data-testid="dataTestId">
            <div
                ref="alertRef"
                id="auto-save-modal"
                class="alert"
                :class="['alert--choice']"
                aria-modal="true"
                :aria-labelledby="'alert-title-' + uniqueId"
                :aria-describedby="'alert-message-' + uniqueId"
                data-testid="alert-modal"
            >
                <div class="alert__choice">
                    <div class="alert__choice-header">
                        <h3 class="alert__choice-title" :id="'alert-title-' + uniqueId" data-testid="alert-title">{{ title }}</h3>
                        <button v-if="showCloseBtn" class="alert__choice-close-btn" @click="handleClose" aria-label="Close" data-testid="alert-close-button">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    <div class="alert__choice-body">
                        <div v-html="safeMessage" class="alert__message alert__message--choice" :id="'alert-message-' + uniqueId" data-testid="alert-message"></div>

                        <div class="alert__choice-grid">
                            <button
                                data-testid="alert-confirm-button"
                                ref="confirmBtnRef"
                                class="alert__choice-btn alert__choice-btn--confirm"
                                @click="handleConfirm"
                                :disabled="props.isLoading"
                                :class="{ 'disabled': props.isLoading }"
                            >
                                <ButtonLoading v-if="props.isLoading" :text="props.loadingText" size="xs" />
                                <div v-else class="alert__choice-btn-content" v-html="safeConfirmText"></div>
                            </button>

                            <button
                                data-testid="alert-cancel-button"
                                ref="cancelBtnRef"
                                class="alert__choice-btn alert__choice-btn--cancel"
                                @click="handleClose"
                                :disabled="props.isLoading"
                                :class="{ 'disabled': props.isLoading }"
                            >
                                <div class="alert__choice-btn-content" v-html="safeCancelText"></div>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<style scoped lang="postcss">
.alert-overlay {
    @apply fixed inset-0 bg-echoes-background-black/50 flex items-center justify-center p-4;
    backdrop-filter: blur(4px);
    animation: fadeIn 0.2s ease;
    isolation: isolate;
    z-index: 10000;
}

.alert {
    @apply bg-echoes-background-gray rounded-[21px] shadow-xl border-echoes-border-light w-full max-w-xl mt-16 flex flex-col overflow-hidden;
    animation: slideIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
    position: relative;
    z-index: 1;
}

.alert--choice {
    /* Default: match standard Alert modal width */
    @apply max-w-3xl border border-white/10;
}

/* Only expand on truly large desktops / ultrawide */
@media (min-width: 1920px) {
    .alert--choice {
        max-width: 48rem; /* ~max-w-3xl */
    }
}

.alert__message {
    @apply text-sm text-gray-600 dark:text-gray-300 leading-relaxed;
}

.alert__message--choice {
    @apply text-gray-400 text-sm md:text-base leading-relaxed;
}

.alert__message--choice :deep(span:first-of-type) {
    @apply block text-gray-400 text-base md:text-lg font-normal;
}

.alert__message--choice :deep(span:last-of-type) {
    @apply text-gray-500 text-xs md:text-sm font-normal;
}

.alert__choice {
    /* Reduce overall footprint on laptop/desktop; expand on very large screens */
    @apply px-6 py-6 md:px-8 md:py-7;
}

.alert__choice-header {
    @apply flex items-start gap-4;
}

.alert__choice-title {
    @apply text-xl md:text-2xl font-semibold text-white;
}

.alert__choice-close-btn {
    @apply ml-auto text-gray-500 hover:text-gray-200 p-2 rounded-full transition-colors;
}

.alert__choice-body {
    @apply mt-4;
}

.alert__choice-grid {
    @apply mt-4 grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-3;
}

.alert__choice-btn {
    @apply w-full text-left rounded-2xl border bg-white/5 px-5 py-5 md:px-6 md:py-6 transition-colors;
    border-color: rgba(255, 255, 255, 0.12);
}

.alert__choice-btn:hover {
    border-color: rgba(255, 255, 255, 0.2);
    background-color: rgba(255, 255, 255, 0.06);
}

.alert__choice-btn:focus-visible {
    outline: 2px solid #f97316;
    outline-offset: 2px;
}

.alert__choice-btn--cancel {
    @apply border-orange-500/60;
}

.alert__choice-btn--cancel:hover {
    @apply border-orange-500;
    background-color: rgba(249, 115, 22, 0.05);
}

.alert__choice-btn-content :deep(span:first-of-type) {
    @apply block text-lg md:text-xl font-semibold text-white;
}

.alert__choice-btn-content :deep(span:last-of-type) {
    @apply block mt-1.5 text-sm md:text-base text-gray-500;
}

.alert__choice-btn--cancel .alert__choice-btn-content :deep(span:first-of-type) {
    @apply text-orange-500;
}

.disabled {
    @apply opacity-50 cursor-not-allowed;
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}

@keyframes slideIn {
    from {
        transform: translateY(20px);
        opacity: 0;
    }
    to {
        transform: translateY(0);
        opacity: 1;
    }
}
</style>
